import { Component, DestroyRef, OnInit } from '@angular/core';
import { ModalService } from '../../services/gui/modal/modal.service';
import { UtilService } from '../../services/util.service';
import { API, ClientDTO, CompanyFullDTO, FixedAspectRatioImageDTO, SingleFileUpload, UserClientFullDTO } from '../../services/api/api';
import { UserService } from '../../services/user.service';
import { RoutingID } from '../../routes/sb-routing-enum';
import { Router } from '@angular/router';
import { Clipping } from '../../components/image/model';
import { ImageUploadModel } from '../../services/gui/image/model';
import { ImageUploadService } from '../../services/gui/image/image-upload.service';
import { TranslateService } from '@ngx-translate/core';
import { ErrorService } from '../../services/error.service';
import { take } from 'rxjs/operators';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { PermissionService } from '../../services/permission.service';

@Component({
  selector: 'sb-page-client',
  templateUrl: './client.component.html',
  styleUrls: ['./client.component.scss'],
  standalone: false,
})
export class ClientComponent implements OnInit {
  RoutingID = RoutingID;
  avatarUrl: string;
  imageUpload: ImageUploadModel;
  public client: ClientDTO;
  iconNames: string[];
  icons: {
    name: string;
    pos: {
      top: any;
      left: any;
      transform: any;
    };
  }[];

  constructor(
    protected readonly permissionService: PermissionService,
    public readonly router: Router,
    private readonly userService: UserService,
    private readonly imageUploadService: ImageUploadService,
    public readonly translate: TranslateService,
    public readonly modal: ModalService,
    public readonly util: UtilService,
    public readonly errorHandler: ErrorService,
    private readonly api: API,
    private readonly destroyRef: DestroyRef
  ) {
    this.avatarUrl = './assets/img/no-profile-man.png';
    this.client = this.userService.user.client;
    this.iconNames = [
      'age',
      'assignment',
      'attributes',
      'dashboard',
      'camera',
      'colorDefinitions',
      'company',
      'customer',
      'globe',
      'graphModel',
      'hammer',
      'home',
      'legacy',
      'location',
      'masterData',
      'motives',
      'megaphoneTypes',
      'networkInfrastructure',
      'network',
      'playerOnline',
      'product',
      'qrCode',
      'securityCode',
      'selectionSettings',
      'shoppingkart',
      'steleAttributes',
      'tracker',
      'treeTrunk',
      'userRoles',
      'uploadAlt',
    ];

    this.icons = [];
    this.imageUpload = new ImageUploadModel();
  }

  ngOnInit() {
    this.refreshFromServer();
    this.initIcons();
    this.shuffleIcons();
  }

  initIcons() {
    for (const iconName of this.iconNames) {
      this.icons.push({
        name: iconName,
        pos: {
          top: 0,
          left: 0,
          transform: '',
        },
      });
    }
  }

  shuffleIcons() {
    for (let i = this.icons.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [this.icons[i], this.icons[j]] = [this.icons[j], this.icons[i]];
    }

    const count = this.icons.length;
    const increment = 100 / count;
    let top = 0;
    let left = 0;

    for (const icon of this.icons) {
      top += increment;
      let variance = increment;
      variance = variance * Math.random();
      if (Math.random() < 0.5) {
        variance = -variance;
      }
      let iconTop = top;
      iconTop += variance;
      icon.pos.top = iconTop + '%';
    }

    for (let i = this.icons.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [this.icons[i], this.icons[j]] = [this.icons[j], this.icons[i]];
    }

    for (const icon of this.icons) {
      left += increment;
      let variance = increment;
      variance = variance * Math.random();
      if (Math.random() < 0.5) {
        variance = -variance;
      }
      let iconLeft = left;
      iconLeft += variance;
      icon.pos.left = iconLeft + '%';
      icon.pos.transform = 'rotate(' + Math.random() * 100 + 'deg)';
    }
  }

  pickProfileImage() {
    this.imageUpload?.destroySubscriptions();
    this.api
      .shared_Company_retrieveCompanyById(this.client.primaryCompany.id)
      .pipe(takeUntilDestroyed(this.destroyRef), take(1))
      .subscribe({
        next: (company: CompanyFullDTO) => {
          this.imageUpload.imageTransfer = this.imageUploadService.imageUpload(this.translate.instant('lang.globals.logo'));
          this.imageUpload.imageTransfer.image = company?.logo;
          this.imageUpload.subClippingChange = this.imageUpload.imageTransfer.clippingChange.subscribe((res: Clipping) => {
            this.clippingChange(res);
          });
          this.imageUpload.subFileChange = this.imageUpload.imageTransfer.fileChange.subscribe((file: SingleFileUpload) => {
            this.fileChange(file);
          });
          this.imageUpload.subDeletePress = this.imageUpload.imageTransfer.deletePress.subscribe(() => {
            this.deletePress();
          });
          this.imageUpload.subModalClosed = this.imageUpload.imageTransfer.modalClosed.subscribe(() => {
            this.uploadModalClosed();
          });
        },
        error: error => {
          this.errorHandler.errorHandler(error);
        },
      });
  }

  clippingChange(pClipping: Clipping) {
    if (pClipping) {
      this.applyClipping(pClipping);
      const transImage: FixedAspectRatioImageDTO = this.imageUpload.imageTransfer.image;
      this.imageUpload.imageTransfer.image = null;
      this.imageUpload.imageTransfer.uploading = true;
      this.api
        .shared_Company_clipLogo(this.client.primaryCompany.id, transImage)
        .pipe(takeUntilDestroyed(this.destroyRef), take(1))
        .subscribe({
          next: (aspectRatioImage: FixedAspectRatioImageDTO) => {
            this.imageUpload.imageTransfer.image = aspectRatioImage;
            this.imageUpload.imageTransfer.uploading = false;
            this.refreshFromServer();
          },
          error: error => {
            this.modal.close('sbNavigationModal');
            this.errorHandler.errorHandler(error);
          },
        });
    }
  }

  applyClipping(pClipping: Clipping) {
    this.imageUpload.imageTransfer.image.clippingTLX = pClipping.clippingTLX;
    this.imageUpload.imageTransfer.image.clippingTLY = pClipping.clippingTLY;
    this.imageUpload.imageTransfer.image.clippingBRX = pClipping.clippingBRX;
    this.imageUpload.imageTransfer.image.clippingBRY = pClipping.clippingBRY;
  }

  fileChange(file: SingleFileUpload) {
    this.imageUpload.imageTransfer.image = null;
    this.imageUpload.imageTransfer.uploading = true;

    this.api
      .shared_Company_uploadLogo(this.client.primaryCompany.id, file)
      .pipe(takeUntilDestroyed(this.destroyRef), take(1))
      .subscribe({
        next: (aspectRatioImage: FixedAspectRatioImageDTO) => {
          this.imageUpload.imageTransfer.image = aspectRatioImage;
          this.imageUpload.imageTransfer.uploading = false;
          this.refreshFromServer();
        },
        error: error => {
          this.modal.close('sbNavigationModal');
          this.errorHandler.errorHandler(error);
        },
      });
  }

  deletePress() {
    this.imageUpload.imageTransfer.image = null;
    this.imageUpload.imageTransfer.uploading = true;
    this.api
      .shared_Company_deleteLogo(this.client.primaryCompany.id)
      .pipe(takeUntilDestroyed(this.destroyRef), take(1))
      .subscribe({
        next: (res: boolean) => {
          if (res) {
            this.imageUpload.imageTransfer.image = null;
          }
          this.imageUpload.imageTransfer.uploading = false;
          this.refreshFromServer();
        },
        error: error => {
          this.modal.close('sbNavigationModal');
          this.errorHandler.errorHandler(error);
        },
      });
  }

  uploadModalClosed() {
    this.imageUpload.destroySubscriptions();
    this.refreshFromServer();
  }

  refreshFromServer() {
    this.api
      .shared_Status_status()
      .pipe(takeUntilDestroyed(this.destroyRef), take(1))
      .subscribe({
        next: (data: UserClientFullDTO) => {
          this.client = data.client;
          this.avatarUrl = this.client?.primaryCompany?.logo
            ? this.util.appendQueryParamToUrl(this.client.primaryCompany.logo.url as unknown as string, 'thumbnail=L')
            : './assets/img/no-profile-man.png';
        },
        error: error => {
          this.errorHandler.errorHandler(error);
        },
      });
  }
}
